import {
  CACHEABLE_PREFIXES,
  EPHEMERAL_MARKER,
  FILTER_PREFIX
} from './keyComposition';

/** Column-config param names, composed under COLUMN_PREFIX (e.g. `pn-c-hidden`). */
export const COLUMN_PARAM = {
  HIDDEN: 'hidden',
  ORDER: 'order'
} as const;

/**
 * True when the key is one of our typed params (filter/column/pagination/sort),
 * regardless of persistence. Use for URL ownership decisions (e.g. clearing).
 */
export const isPanoramaQueryKey = (key: string): boolean =>
  CACHEABLE_PREFIXES.some(p => key.startsWith(p));

/**
 * True when the key should be persisted to the bag: a Panorama key that is not
 * marked ephemeral. Use for caching decisions (snapshot, restore, skip-restore).
 */
export const isPersistableQueryKey = (key: string): boolean =>
  isPanoramaQueryKey(key) && !key.endsWith(EPHEMERAL_MARKER);

export const PERSISTED_QUERY_PARAMS_STORAGE_KEY =
  'panorama:persisted_query_params';

/**
 * True when `key` is a filter param (`pn-f-`) scoped to the given table suffix.
 * The suffix rides as a trailing `-<suffix>` segment and filter names are
 * snake_case (no hyphens), so the first hyphen after the prefix marks the
 * suffix boundary. A trailing ephemeral marker is ignored. Lets the URL
 * transport clear/replace a single table's filters without knowing filterConfig.
 */
export const isFilterKeyForSuffix = (key: string, suffix?: string): boolean => {
  if (!key.startsWith(FILTER_PREFIX)) return false;
  const rest = key
    .slice(FILTER_PREFIX.length)
    .replace(new RegExp(`${EPHEMERAL_MARKER}$`), '');
  const dashIndex = rest.indexOf('-');
  const keySuffix = dashIndex === -1 ? undefined : rest.slice(dashIndex + 1);
  return keySuffix === suffix;
};
